import React, { useState, useEffect } from 'react';
import Modal from './Modal';

export interface InputDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (value: string) => void;
  title: string;
  message?: string;
  placeholder?: string;
  defaultValue?: string;
  submitText?: string;
  cancelText?: string;
  required?: boolean;
  /** When provided, renders a dropdown constrained to these values instead of a free-text input. */
  options?: string[];
}

const InputDialog: React.FC<InputDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  message,
  placeholder,
  defaultValue = '',
  submitText = 'Submit',
  cancelText = 'Cancel',
  required = true,
  options
}) => {
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (required && !value.trim()) {
      return;
    }
    onSubmit(value);
    onClose();
    setValue('');
  };

  const handleCancel = () => {
    onClose();
    setValue('');
  };

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} title={title} size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        {message && (
          <p className="text-[#b9ad9b] text-sm">
            {message}
          </p>
        )}

        <div>
          {options ? (
            <select
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="bg-[#17140f] text-[#f3ede2] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
              autoFocus
            >
              <option value="">{placeholder || 'Select an option'}</option>
              {options.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
              autoFocus
            />
          )}
        </div>

        <div className="flex items-center justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 text-[#b9ad9b] bg-transparent border border-[#3a3122] rounded-lg hover:bg-[#28221a] transition-colors"
          >
            {cancelText}
          </button>
          <button
            type="submit"
            disabled={required && !value.trim()}
            className="px-4 py-2 text-white bg-[#e2916d] hover:bg-[#f0a67d] disabled:bg-[#8a7f6e] disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            {submitText}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default InputDialog;